import clsx from 'clsx';
import { Form, Formik } from 'formik';
import React, { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import ReactHtmlParser from 'react-html-parser';
import { useQueryClient } from 'react-query';
import * as Yup from 'yup';

import MentionableInput from '@components/Form/MentionableInput';
import { useCommentsContext } from '@contexts/CommentsContext';
import useCommentCreate from '@hooks/useCommentCreate';
import useCommentUpdate from '@hooks/useCommentUpdate';
import useCurrentUser from '@hooks/useCurrentUser';
import useEventTracking from '@hooks/useEventTracking';
import { ClickTarget, TypingInput, click, typing } from '@tracking';
import { Comment, CommentableType, NewComment, getCommentsKey } from '@utils/backend-api/comments';

import { PrimaryButton } from '../../../Button/variants';
import EnableProfileModal from '../../../EnableProfileModal';
import AnonymousPostBlock from '../../../pages/Forum/EditForumPostPage/AnonymousPostBlock';

const validationSchema = () =>
  Yup.object({
    body: Yup.string().required('Comment body is required'),
    is_anonymous: Yup.boolean(),
  });

// TODO: Disable commenting when replies_per_comment limit has been reached.
// See api/comments_controller#index
export default function CommentForm({
  comment,
  commentableId,
  commentableType,
  replyingTo,
  hideReplyForm,
  hideEditForm,
}: {
  comment?: Comment;
  commentableId: number;
  commentableType: CommentableType;
  replyingTo?: string;
  hideReplyForm?: () => void;
  hideEditForm?: () => void;
}) {
  const { currentUser } = useCurrentUser();
  const { mutateAsync: createAsync } = useCommentCreate();
  const { mutateAsync: updateAsync } = useCommentUpdate();
  const queryClient = useQueryClient();
  const { rootCommentableId, rootCommentableType } = useCommentsContext();
  const [canInitiallyComment] = useState(currentUser?.can_actually_comment);
  const [commentStarted, setCommentStarted] = useState(false);
  const { trackEvent } = useEventTracking();
  const isEditing = !!comment;

  const editBody = useMemo(() => {
    if (!comment?.body) return '';
    const body = ReactHtmlParser(comment.body);
    // Transform mentioned user links back to @user mention format
    return body
      .map((node) => {
        if (node.type === 'a' && node.props.className === 'mention') {
          const hrefParts = node.props.href.split('/');
          // The last part of the href is the username
          return `@${hrefParts[hrefParts.length - 1]}`;
        } else if (node.type === 'a') {
          return node.props.href;
        }
        return node;
      })
      .join('');
  }, [comment?.body]);

  const INITIAL_VALUES = {
    body: replyingTo ? `@${replyingTo} ` : editBody,
    is_anonymous: false,
  };

  const handleCommentStart = () => {
    if (!commentStarted) {
      trackEvent(typing({ inputTarget: replyingTo ? TypingInput.Reply : TypingInput.Comment }));
      setCommentStarted(true);
    }
  };

  const handleSubmit = async ({ body, is_anonymous }, { resetForm }) => {
    const data: NewComment = {
      body,
      commentable_id: commentableId,
      commentable_type: commentableType,
      is_anonymous,
    };

    if (isEditing) {
      return updateAsync(
        { id: comment.id, body, commentable_id: commentableId, commentable_type: commentableType },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(getCommentsKey(rootCommentableId, rootCommentableType));
            toast.success('Your comment has been updated.');
            resetForm();
            hideEditForm();
          },
          onError: (e) => {
            console.error(e);
            toast.error('Comment was not updated. Please try again.');
          },
        }
      );
    }

    return createAsync(data, {
      onSuccess: () => {
        queryClient.invalidateQueries(getCommentsKey(rootCommentableId, rootCommentableType));
        toast.success('Your comment has been created!');
        trackEvent(
          click({ clickTarget: replyingTo ? ClickTarget.CommentReply : ClickTarget.CommentPost })
        );
        resetForm();
        if (hideReplyForm) hideReplyForm();
      },
      onError: (e) => {
        console.error(e);
        toast.error('Comment was not created. Please try again.');
      },
    });
  };

  const firstName = currentUser?.name?.split(' ')[0] || 'You';
  let newCommentButtonText;
  if (replyingTo) {
    newCommentButtonText = 'Reply';
  } else if (commentableType === CommentableType.ForumPost) {
    newCommentButtonText = `Post new comment as ${firstName}`;
  } else {
    newCommentButtonText = 'Post';
  }
  const buttonText = isEditing ? 'Save' : newCommentButtonText;

  return (
    <Formik
      enableReinitialize
      initialValues={{ ...INITIAL_VALUES }}
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
    >
      {({ isSubmitting, isValid, submitForm, values, handleChange }) => (
        <Form noValidate id="comment-form" className={clsx({ '--reply': !!replyingTo })}>
          <div className="relative">
            <MentionableInput
              name="body"
              placeholder="Post a comment"
              required={true}
              autoFocus={!!replyingTo}
              onChange={(e) => {
                handleChange(e);
                handleCommentStart();
              }}
              className="comment-editor"
              value={values.body}
              contentId={commentableId}
              contentType={commentableType}
            />
          </div>
          <div className="mt-8 flex justify-end">
            <div className="flex flex-col items-end">
              {currentUser?.can_actually_comment && (
                <>
                  <PrimaryButton
                    type="submit"
                    as="button"
                    size="small"
                    className={clsx('button-submit min-w-[120px]', {
                      'bg-grayscale-charcoal':
                        values.is_anonymous && commentableType == CommentableType.ForumPost,
                    })}
                    disabled={isSubmitting || !isValid}
                  >
                    {values.is_anonymous ? `Post anonymous comment` : buttonText}
                  </PrimaryButton>
                  {!isEditing && commentableType === CommentableType.ForumPost && (
                    <AnonymousPostBlock isAnonymous={values.is_anonymous} justify="right" />
                  )}
                </>
              )}
              {!canInitiallyComment && (
                // Keep modal in DOM to avoid resetting it on re-render
                <div
                  className={clsx({
                    ['!hidden']: currentUser?.can_actually_comment,
                  })}
                >
                  <EnableProfileModal
                    isLoggedIn={!!currentUser}
                    isSubscriber={currentUser?.is_subscriber}
                    isTriggeredByComment
                    commentButtonText={
                      commentableType === CommentableType.Article
                        ? newCommentButtonText
                        : buttonText
                    }
                    isCommentForForum={commentableType === CommentableType.ForumPost}
                    handleSubmitComment={submitForm}
                  />
                </div>
              )}
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
}
